import type { NotificationWithStatus } from '@/services/notification';
import { cn } from '@/lib/utils';

interface NotificationDetailModalProps {
  isOpen: boolean;
  notification?: NotificationWithStatus | null;
  onClose: () => void;
}

// 공용 알림 상세 모달 (모바일/데스크톱 모두 사용)
export function NotificationDetailModal({
  isOpen,
  notification,
  onClose
}: NotificationDetailModalProps) {
  if (!isOpen || !notification) return null;

  const sentAt =
    notification.sent_at ||
    notification.created_at ||
    notification.data?.sent_at ||
    null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        className={cn(
          'w-full max-w-lg bg-white rounded-2xl shadow-xl overflow-hidden',
          'max-h-[90vh] min-h-[320px] flex flex-col'
        )}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900 truncate">
            {notification.title || '알림'}
          </h2>
          <button
            aria-label="닫기"
            onClick={onClose}
            className="p-2 rounded-full hover:bg-gray-100"
          >
            <svg
              className="w-5 h-5 text-gray-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="px-5 py-4 flex-1">
          <div className="bg-gray-50 rounded-xl p-4 md:p-5 h-full overflow-y-auto text-sm text-gray-800 leading-relaxed whitespace-pre-wrap">
            {notification.message || '내용이 없습니다.'}
          </div>
        </div>

        <div className="px-5 py-3 border-t border-gray-200 text-xs text-gray-500 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 font-medium">
              {notification.type}
            </span>
            {notification.target_role && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-green-50 text-green-700 font-medium">
                {notification.target_role}
              </span>
            )}
          </div>
          {sentAt && (
            <span>
              {new Date(sentAt).toLocaleString('ko-KR', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit'
              })}
            </span>
          )}
        </div>

        <div className="px-5 py-3 border-t border-gray-200">
          <button
            onClick={onClose}
            className="w-full py-3 rounded-xl bg-green-500 text-white font-semibold hover:bg-green-600 transition-colors"
          >
            닫기
          </button>
        </div>
      </div>
    </div>
  );
}
